<template>
  <v-app>
    <v-container fluid class="pa-2 pa-sm-3">

      <!-- Search Filter -->
      <v-card class="mb-3 pa-3">
        <div class="subheading font-weight-bold mb-2">Search Earnings by Product & Date</div>
        <v-layout row wrap align-center>
          <v-flex xs12 sm6 md3 class="pr-1 pb-1">
            <date-picker v-model="start" :get-classes="getRangeClasses" :default-value="end || new Date()"
              :disabled-date="disabledStartDate" type="date" placeholder="Start Date" style="width:100%;"/>
          </v-flex>
          <v-flex xs12 sm6 md3 class="pr-1 pb-1">
            <date-picker v-model="end" :get-classes="getRangeClasses" :default-value="start || new Date()"
              :disabled-date="disabledEndDate" type="date" placeholder="End Date" style="width:100%;"/>
          </v-flex>
          <v-flex xs12 sm6 md4 class="pr-1 pb-1">
            <v-select v-if="allProduct" v-model="product_id" :items="transformProduct(allProduct)"
              item-text="text" item-value="value" label="Select Product" hide-details/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pb-1">
            <v-btn color="primary" block @click="validate">Search</v-btn>
          </v-flex>
        </v-layout>
        <div v-if="result" class="mt-2 title font-weight-bold primary--text">{{ result }}</div>
      </v-card>

      <!-- Chart -->
      <v-card class="mb-3 pa-2" v-if="activeEarnings.length">
        <v-card-title class="subheading font-weight-bold pb-1">Active Product Earnings</v-card-title>
        <canvas id="productEarningsChart" height="120"></canvas>
      </v-card>

      <!-- Active Table -->
      <v-card class="mb-3">
        <v-card-title class="subheading font-weight-bold" style="background:#e8f5e9;">
          Active Products (canceled = false)
        </v-card-title>
        <div style="overflow-x:auto;">
          <table style="width:100%; border-collapse:collapse; font-size:13px;">
            <thead>
              <tr style="background:#2e7d32; color:#fff;">
                <th style="padding:10px; text-align:left;">Product</th>
                <th style="padding:10px; text-align:right;">Total Investment</th>
                <th style="padding:10px; text-align:right;">Total Interest</th>
                <th style="padding:10px; text-align:right;">Interest Rate</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="activeEarnings.length">
                <tr v-for="(item, i) in activeEarnings" :key="i" :style="i%2===0?'background:#f9f9f9':''">
                  <td style="padding:8px 10px;">{{ item.name }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.amount) }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.interest) }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ item.interest_rate | percent(2) }}</td>
                </tr>
                <tr style="background:#c8e6c9; font-weight:bold;">
                  <td style="padding:8px 10px;">TOTAL</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(activeEarnings,'amount')) }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(activeEarnings,'interest')) }}</td>
                  <td></td>
                </tr>
              </template>
              <tr v-else><td colspan="4" style="padding:12px; text-align:center; color:#999;">No active data</td></tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- Inactive Table -->
      <v-card>
        <v-card-title class="subheading font-weight-bold" style="background:#fce4ec;">
          Inactive Products (canceled = true)
        </v-card-title>
        <div style="overflow-x:auto;">
          <table style="width:100%; border-collapse:collapse; font-size:13px;">
            <thead>
              <tr style="background:#b71c1c; color:#fff;">
                <th style="padding:10px; text-align:left;">Product</th>
                <th style="padding:10px; text-align:right;">Total Investment</th>
                <th style="padding:10px; text-align:right;">Total Interest</th>
                <th style="padding:10px; text-align:right;">Interest Rate</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="inactiveEarnings.length">
                <tr v-for="(item, i) in inactiveEarnings" :key="i" :style="i%2===0?'background:#f9f9f9':''">
                  <td style="padding:8px 10px;">{{ item.name }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.amount) }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.interest) }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ item.interest_rate | percent(2) }}</td>
                </tr>
                <tr style="background:#f8bbd0; font-weight:bold;">
                  <td style="padding:8px 10px;">TOTAL</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(inactiveEarnings,'amount')) }}</td>
                  <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(inactiveEarnings,'interest')) }}</td>
                  <td></td>
                </tr>
              </template>
              <tr v-else><td colspan="4" style="padding:12px; text-align:center; color:#999;">No inactive data</td></tr>
            </tbody>
          </table>
        </div>
      </v-card>

    </v-container>

    <v-snackbar v-model="toast.show" top right color="black">
      {{ toast.msg }}
      <v-btn flat dark small @click.native="toast.show = false">Close</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import productMixin from '../../mixins/products.mixin';
import Vue from 'vue';

export default {
  name: 'ProductEarnings',
  mixins: [productMixin],
  data() {
    return {
      valid: true, start: null, end: null, product_id: null,
      result: null,
      toast: { show: false, msg: '' },
      chartInstance: null,
    };
  },
  computed: {
    ...mapGetters(['allProduct', 'getAllProductEarnings']),
    activeEarnings() {
      return (this.getAllProductEarnings || []).filter(i => i.status === 'active');
    },
    inactiveEarnings() {
      return (this.getAllProductEarnings || []).filter(i => i.status === 'inactive');
    },
  },
  watch: {
    getAllProductEarnings(val) {
      if (val) this.$nextTick(() => this.renderChart());
    },
  },
  created() {
    this.getAllProducts();
    this.fetchAllProductEarnings();
  },
  mounted() {
    if (!window.Chart) {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/chart.js@3.9.1/dist/chart.min.js';
      s.onload = () => this.renderChart();
      document.head.appendChild(s);
    } else {
      this.renderChart();
    }
  },
  methods: {
    ...mapActions(['getAllProducts', 'fetchAllProductEarnings']),
    sumField(arr, field) {
      return arr.reduce((s, i) => s + parseFloat(i[field] || 0), 0);
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
    },
    getRangeClasses(cellDate, currentDates, classnames) {
      const classes = [];
      const start = this.start && new Date(this.start).setHours(0,0,0,0);
      const end = this.end && new Date(this.end).setHours(0,0,0,0);
      if (!/disabled|active|not-current-month/.test(classnames) && start && end &&
        cellDate.getTime() >= start && cellDate.getTime() <= end) classes.push('in-range');
      return classes;
    },
    disabledStartDate(date) {
      return this.end && new Date(date).setHours(0,0,0,0) > new Date(this.end).setHours(0,0,0,0);
    },
    disabledEndDate(date) {
      return this.start && new Date(date).setHours(0,0,0,0) < new Date(this.start).setHours(0,0,0,0);
    },
    renderChart() {
      if (!window.Chart || !this.activeEarnings.length) return;
      const ctx = document.getElementById('productEarningsChart');
      if (!ctx) return;
      if (this.chartInstance) this.chartInstance.destroy();
      this.chartInstance = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.activeEarnings.map(i => i.name),
          datasets: [
            { label: 'Investment', data: this.activeEarnings.map(i => parseFloat(i.amount || 0)), backgroundColor: '#1565c0' },
            { label: 'Interest', data: this.activeEarnings.map(i => parseFloat(i.interest || 0)), backgroundColor: '#43a047' },
          ],
        },
        options: { responsive: true, plugins: { legend: { position: 'top' } } },
      });
    },
    validate() {
      if (!this.start || !this.end || !this.product_id) {
        this.toast.msg = 'Please select start date, end date and product.';
        this.toast.show = true;
        return;
      }
      this.valid = false;
      Vue.axios.post('admin/product-earnings-total', {
        start: this.start, end: this.end, product_id: this.product_id,
      }).then((res) => {
        this.result = this.formatCurrency(res.data.data[0].total);
        this.toast.msg = 'Result fetched successfully';
        this.toast.show = true;
        this.valid = true;
      }).catch(() => { this.valid = true; });
    },
  },
};
</script>

<style scoped>
input.mx-input { border: 1px solid #ccc; }
</style>
